/* global BigInt */
// Mirrored in Player, Pricer and Cloud. Keep the shared CALC fixture tests identical.
export const CALC_CAPABILITY = "calculatedTotalV1";
const clone = value => value == null ? value : JSON.parse(JSON.stringify(value));
const fail = message => { const error = new Error(message); error.code = "CALC_INVALID"; throw error; };
const present = value => value !== undefined && value !== null && String(value).trim() !== "";
export const isCalculatedPricing = (source = {}) => Boolean(source && (
	source.pricingBehavior === "calculatedTotal" || source.pricingAmountMode === "fixedTotal" || source.calculation
	|| source.hit?.pricingBehavior === "calculatedTotal" || source.hit?.calculation
));
export const calculationFields = (source = {}, fallback = {}) => {
	const sources = [source, source.raw, source.hit, source.extraFields, fallback, fallback.raw, fallback.hit].filter(Boolean);
	const result = {};
	["pricingBehavior", "pricingAmountMode", "calculationDefinition", "calculation", "skipPricingMode"].forEach(key => {
		const owner = sources.find(item => Object.prototype.hasOwnProperty.call(item, key));
		if (owner) result[key] = clone(owner[key]);
	});
	if (typeof result.calculationDefinition === "string") {
		try { result.calculationDefinition = JSON.parse(result.calculationDefinition); }
		catch (_) { fail("This catalog calculation definition is invalid. Refresh the pricing catalog."); }
	}
	return result;
};
const parseDecimal = (value, precision, label) => {
	const text = String(value == null ? "" : value).trim();
	if (!/^\d+(\.\d+)?$/.test(text)) fail(`Enter a nonnegative ${label}.`);
	const [whole, fraction = ""] = text.split(".");
	if (fraction.length > precision) fail(`${label} allows at most ${precision} decimal places.`);
	if (whole.length > 12) fail(`${label} is too large.`);
	return { text, integer: BigInt(whole + fraction.padEnd(precision, "0")), precision };
};
const safeMinor = value => {
	if (value < BigInt(0) || value > BigInt(Number.MAX_SAFE_INTEGER)) fail("The final amount is too large.");
	return Number(value);
};
export const parseFinalAmountMinor = value => safeMinor(parseDecimal(value, 2, "final amount").integer);
export function calculateTotalMinor(quantity, rate) {
	const q = parseDecimal(quantity, 4, "measurement");
	const r = parseDecimal(rate, 6, "rate");
	if (q.integer <= BigInt(0)) fail("Measurement must be greater than zero. Use Remove to clear a selection.");
	// q is scaled by 10^4 and r by 10^6. Round the product once to cents, half up.
	return safeMinor((q.integer * r.integer + BigInt(50000000)) / BigInt(100000000));
}
export const formatCalcAmount = minor => (Number(minor) / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
export const createCalculationOperationId = () => `calc_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
export function validateCalculation(calculation) {
	const c = calculation;
	if (!c || c.schemaVersion !== 1 || c.currency !== "USD" || c.formula !== "quantityTimesRate") fail("This calculation requires a compatible pricing editor.");
	if (!["calculated", "manual"].includes(c.mode) || !Number.isSafeInteger(c.revision) || c.revision < 1 || !c.lastOperationId) fail("The saved calculation is incomplete.");
	if (!Number.isSafeInteger(c.effectiveTotalMinor) || c.effectiveTotalMinor < 0) fail("The saved final amount is invalid.");
	if (c.inputs) {
		if (!["sq_ft", "linear_ft"].includes(c.unit)) fail("The measurement unit has not been confirmed.");
		if (calculateTotalMinor(c.inputs.quantityDecimal, c.inputs.rateDecimal) !== c.computedTotalMinor) fail("The saved calculation inputs do not match its total.");
	} else if (c.computedTotalMinor !== null || c.mode === "calculated") fail("Calculation inputs are required.");
	if (c.mode === "calculated" && c.effectiveTotalMinor !== c.computedTotalMinor) fail("The calculated amount does not match the final amount.");
	if (c.mode === "manual" && c.effectiveTotalMinor !== c.manualTotalMinor) fail("The override does not match the final amount.");
	if (!c.source || !c.source.pricingSnapshotId || !c.source.objectID) fail("The original catalog source is missing.");
	return c;
}
export function createCalculationDraft(source = {}) {
	const fields = calculationFields(source);
	const c = fields.calculation;
	const definition = fields.calculationDefinition || {};
	return {
		mode: c?.mode || "manual",
		quantity: c?.inputs?.quantityDecimal || "",
		rate: c?.inputs?.rateDecimal ?? c?.source?.catalogRateDecimal ?? definition.catalogRateDecimal ?? "",
		amount: c ? (c.effectiveTotalMinor / 100).toFixed(2) : "",
		reason: "",
		expectedRevision: c?.revision || 0
	};
}
export function applyCalculationDraft(source = {}, draft = {}, options = {}) {
	const fields = calculationFields(source);
	const previous = fields.calculation;
	const definition = fields.calculationDefinition || {};
	if (!isCalculatedPricing({ ...source, ...fields })) fail("This option does not support calculated amounts.");
	if (previous) validateCalculation(previous);
	if (!previous && (definition.schemaVersion !== 1 || (definition.formula && definition.formula !== "quantityTimesRate"))) fail("This catalog calculation requires a compatible pricing editor.");
	if (Number(draft.expectedRevision) !== (previous?.revision || 0)) fail("This amount changed. Reopen the calculator before applying your edit.");
	const unit = previous?.unit || definition.measurementUnit || "";
	const sourceBasis = clone(previous?.source || {
		pricingSnapshotId: definition.pricingSnapshotId || options.pricingSnapshotId || "",
		objectID: source.objectID || source.offerId || "",
		sku: source.sku || source.productId || "",
		model: definition.model || "",
		catalogRateDecimal: String(definition.catalogRateDecimal ?? ""),
		catalogUnit: unit,
		recordFingerprint: definition.recordFingerprint || ""
	});
	if (!sourceBasis.pricingSnapshotId || !sourceBasis.objectID) fail("A verified pricing snapshot and catalog offer are required.");
	let inputs = null;
	let computedTotalMinor = null;
	if (previous?.inputs && !present(draft.quantity)) fail("Keep the saved measurement when overriding the final amount. Remove the selection to start a new calculation.");
	if (present(draft.quantity)) {
		if (!["sq_ft", "linear_ft"].includes(unit)) fail("Confirm the measurement unit before using the calculator.");
		computedTotalMinor = calculateTotalMinor(draft.quantity, draft.rate);
		const rateChanged = Number(draft.rate) !== Number(sourceBasis.catalogRateDecimal);
		if (rateChanged && definition.allowRateOverride !== true) fail("Rate changes are not enabled for this option.");
		inputs = { quantityDecimal: String(draft.quantity).trim(), rateDecimal: String(draft.rate).trim(), rateOrigin: rateChanged ? "override" : "catalog" };
	}
	const mode = draft.mode;
	if (!["manual", "calculated"].includes(mode)) fail("Choose a final amount or use the calculated amount.");
	if (mode === "calculated" && computedTotalMinor === null) fail("Enter a measurement before using the calculated amount.");
	const manualTotalMinor = mode === "manual" ? parseFinalAmountMinor(draft.amount) : null;
	const effectiveTotalMinor = mode === "manual" ? manualTotalMinor : computedTotalMinor;
	const reason = String(draft.reason || "").trim();
	if ((mode === "manual" || inputs?.rateOrigin === "override" || effectiveTotalMinor === 0) && !reason) fail("Add an internal reason for this amount or rate override.");
	const calculation = {
		schemaVersion: 1, revision: (previous?.revision || 0) + 1, basedOnRevision: previous?.revision || 0,
		formula: "quantityTimesRate", mode, currency: "USD", unit, source: sourceBasis, inputs,
		computedTotalMinor, manualTotalMinor, effectiveTotalMinor, rounding: "USD-cent-half-up-v1", reason,
		lastOperationId: options.operationId || createCalculationOperationId(),
		updatedBy: options.actorId || "pricing-editor", updatedAt: options.now || new Date().toISOString()
	};
	validateCalculation(calculation);
	return normalizeCalculatedInvoice({ ...source, ...fields, calculation, pricingBehavior: "calculatedTotal" });
}
export function normalizeCalculatedInvoice(source = {}) {
	if (!isCalculatedPricing(source)) return source;
	const fields = calculationFields(source);
	const c = validateCalculation(fields.calculation);
	const amount = c.effectiveTotalMinor / 100;
	const result = { ...source, ...fields, pricingBehavior: "calculatedTotal", pricingAmountMode: "fixedTotal", skipPricingMode: true,
		qty: 1, quantity: 1, rate: amount, value: amount, powerUserPrice: amount, valueFormatted: formatCalcAmount(c.effectiveTotalMinor), discount: 1, quantityDiscounts: [], roundUp: false };
	["material", "markup", "labor", "labortime", "finish", "credit", "rateParts", "pricingRateReference", "pricingCreditMode", "pricingModeBaseRate", "wholesaleRate", "baseRate", "baseValue", "pricingBaseValue", "pricingModeTierBase", "pricingModeDetails"].forEach(key => { delete result[key]; });
	return result;
}
export function assertCalculationRevision(previous, next) {
	const offer = row => row?.offerId || row?.objectID || row?.calculation?.source?.objectID || row?.productId || row?.sku;
	const sameOffer = !previous || !offer(previous) || !offer(next) || offer(previous) === offer(next);
	if (!isCalculatedPricing(next)) {
		if (sameOffer && isCalculatedPricing(previous)) fail("A calculated amount cannot be replaced by an ordinary unit price.");
		return;
	}
	const c = validateCalculation(calculationFields(next).calculation);
	const old = sameOffer ? calculationFields(previous || {}).calculation : null;
	if (old && JSON.stringify(old.source) !== JSON.stringify(c.source)) fail("The original catalog basis cannot change when editing a saved calculation.");
	if (old && c.lastOperationId === old.lastOperationId) {
		if (JSON.stringify(c) !== JSON.stringify(old)) fail("A repeated calculation operation has different inputs.");
		return;
	}
	if (c.basedOnRevision !== (old?.revision || 0) || c.revision !== (old?.revision || 0) + 1) fail("This amount changed in another editor. Reopen it before applying your edit.");
}
export function validateCalculatedInvoiceChanges(previous = [], next = []) {
	const previousByLine = new Map(previous.map(row => [row.lineId || row.id, row]));
	const seen = new Set();
	next.forEach(row => {
		if (!isCalculatedPricing(row) && !isCalculatedPricing(previousByLine.get(row.lineId || row.id))) return;
		const key = row.lineId || row.id;
		if (!key || seen.has(key)) fail("Calculated selections require unique, stable line identities.");
		seen.add(key);
		assertCalculationRevision(previousByLine.get(key), row);
	});
}
export function appendCalculationAudit(events = [], previous = [], next = [], context = {}) {
	const result = clone(events || []);
	const previousByLine = new Map(previous.map(row => [row.lineId || row.id, row]));
	const nextByLine = new Map(next.map(row => [row.lineId || row.id, row]));
	new Set([...previousByLine.keys(), ...nextByLine.keys()]).forEach(lineId => {
		const before = calculationFields(previousByLine.get(lineId) || {}).calculation || null;
		const after = calculationFields(nextByLine.get(lineId) || {}).calculation || null;
		if ((!before && !after) || JSON.stringify(before) === JSON.stringify(after)) return;
		const operationId = after?.lastOperationId || context.operationId || createCalculationOperationId();
		if (result.some(event => event.lineId === lineId && event.operationId === operationId)) return;
		result.push({ lineId, operationId, action: !after ? "remove" : !before ? "apply" : "revise", before: clone(before), after: clone(after), actorId: context.actorId || after?.updatedBy || "pricing-editor", at: context.now || after?.updatedAt || new Date().toISOString() });
	});
	return result;
}
const CUSTOMER_FIELDS = ["id", "lineId", "objectID", "offerId", "sku", "productId", "description", "desc", "label", "name", "photo", "note", "manufacturer", "model", "color", "listId", "listName", "sectionId", "sectionName", "itemId", "itemName", "settlesContractLineId", "allowanceSettlement", "invoiceGroup", "invoiceGroupOrder"];
export function projectCalculatedCustomerLine(source = {}) {
	if (!isCalculatedPricing(source)) return source;
	const c = calculationFields(source).calculation;
	const result = {};
	CUSTOMER_FIELDS.forEach(key => { if (source[key] !== undefined) result[key] = clone(source[key]); });
	result.pricingBehavior = "calculatedTotal";
	result.customerDisplay = "totalOnly";
	if (!c && source.customerDisplay === "totalOnly" && source.qty === 1 && Number.isFinite(source.value) && source.value >= 0) {
		return { ...result, qty: 1, rate: source.value, value: source.value, discount: 1, skipPricingMode: true, valueFormatted: formatCalcAmount(Math.round(source.value * 100)) };
	}
	if (!c) return { ...result, qty: null, rate: null, value: null, amountRequired: true, priceLabel: "Amount required", skipPricingMode: true };
	validateCalculation(c);
	return { ...result, qty: 1, rate: c.effectiveTotalMinor / 100, value: c.effectiveTotalMinor / 100, discount: 1, skipPricingMode: true, valueFormatted: formatCalcAmount(c.effectiveTotalMinor) };
}
export function projectCustomerPricingPayload(value) {
	if (Array.isArray(value)) return value.map(projectCustomerPricingPayload);
	if (!value || typeof value !== "object" || value instanceof Date) return value;
	if (isCalculatedPricing(value)) return projectCalculatedCustomerLine(value);
	return Object.keys(value).reduce((result, key) => {
		if (!["calculationAudit", "calculationDefinition", "calculation"].includes(key)) result[key] = projectCustomerPricingPayload(value[key]);
		return result;
	}, {});
}

// Undo/redo is a new edit against the current line revision, never a replay of an old operation.
export function restoreCalculationRows(previous = [], desired = []) {
	return desired.map(row => {
		if (!isCalculatedPricing(row)) return row;
		const old = previous.find(item => (item.lineId || item.id) === (row.lineId || row.id));
		const saved = validateCalculation(calculationFields(row).calculation);
		const current = calculationFields(old || {}).calculation;
		if (current && JSON.stringify(current) === JSON.stringify(saved)) return row;
		const sameOffer = current?.source?.objectID === saved.source.objectID;
		const revision = sameOffer ? current.revision : 0;
		const calculation = { ...clone(saved), revision: revision + 1, basedOnRevision: revision,
			lastOperationId: createCalculationOperationId(), updatedAt: new Date().toISOString(),
			reason: `Restored a previous amount. ${saved.reason || ""}`.trim() };
		return normalizeCalculatedInvoice({ ...row, calculation });
	});
}

// Validate the whole group before the legacy bridge clears or rebuilds any rows.
export function validateCalculatedSelectionGroup({ previousRows = [], selections = [], expectedSelections = [], groupId, multi = false, staticSelections = [] }) {
	const keyFor = selection => multi ? `${groupId}__multi__${selection.objectID || selection.offerId}` : groupId;
	expectedSelections.forEach(expected => {
		const current = previousRows.find(row => (row.lineId || row.id) === keyFor(expected));
		if (!current || JSON.stringify(calculationFields(current).calculation) !== JSON.stringify(calculationFields(expected).calculation)) fail("This selection changed. Reopen it before replacing or removing the calculated amount.");
	});
	const seen = new Set();
	selections.forEach(selection => {
		const catalog = staticSelections.find(row => row.objectID === (selection.objectID || selection.offerId));
		if (isCalculatedPricing(catalog) && !isCalculatedPricing(selection)) fail("This option requires a compatible calculator. Refresh the pricing editor before selecting it.");
	});
	return selections.filter(isCalculatedPricing).map(selection => {
		const objectID = selection.objectID || selection.offerId;
		if (!objectID || seen.has(objectID)) fail("Only one calculation per offer and option group is supported.");
		seen.add(objectID);
		const key = keyFor(selection);
		const previous = previousRows.find(row => (row.lineId || row.id) === key);
		assertCalculationRevision(previous, selection);
		const c = calculationFields(selection).calculation;
		if (c.source.objectID !== objectID) fail("The calculation does not match this catalog offer.");
		if (!isCalculatedPricing(previous) || previous?.calculation?.source?.objectID !== objectID) {
			const catalog = staticSelections.find(row => row.objectID === objectID);
			const definition = calculationFields(catalog || {}).calculationDefinition;
			if (!catalog || !isCalculatedPricing(catalog) || !definition || definition.schemaVersion !== 1
				|| definition.pricingSnapshotId !== c.source.pricingSnapshotId
				|| definition.measurementUnit !== c.unit
				|| String(definition.catalogRateDecimal) !== String(c.source.catalogRateDecimal)
				|| definition.recordFingerprint !== c.source.recordFingerprint) fail("This calculation is not supported by the current option group and pricing snapshot.");
		}
		return key;
	});
}
